import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { type FormQuestionState } from '@pixacare/forms';
import { TuiCheckbox, TuiInput, TuiSelectLike } from '@taiga-ui/core';
import { TuiBlock, TuiChevron, TuiDataListWrapper, TuiInputChip, TuiMultiSelect } from '@taiga-ui/kit';
import { FormDisplayMode } from '../../form-grid/models/form-display-mode';

@Component({
  selector: 'pxc-form-question-checkbox',
  templateUrl: './form-question-checkbox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TuiInput,
    TuiSelectLike,
    TuiBlock,
    TuiCheckbox,
    TuiChevron,
    TuiDataListWrapper,
    TuiInputChip,
    TuiMultiSelect,
  ],
})
export class FormQuestionCheckboxComponent {
  private readonly translocoService = inject(TranslocoService);
  protected readonly hasSelection = signal(false);

  readonly control = input.required<FormGroup>();
  readonly question = input.required<FormQuestionState>();
  readonly mode = input<FormDisplayMode>('form');
  readonly readOnly = input<boolean>(false);

  readonly isGrid = computed(() => this.mode() === 'grid');
  readonly placeholder = computed(
    () => this.question().parameters?.placeholder ?? this.translocoService.translate('Choisir une réponse'),
  );
  readonly choiceKeys = computed(() => this.question().choices?.map((c) => c.key) ?? []);

  private readonly choiceLabels = computed(() => {
    const map = new Map<string, string>();
    for (const choice of this.question().choices ?? []) {
      map.set(choice.key, choice.value);
    }
    return map;
  });

  readonly stringifyChoice = (key: string): string => this.choiceLabels().get(key) ?? key;

  readonly selectedKeys = computed(() =>
    Object.entries(this.control().value ?? {})
      .filter(([, checked]) => checked === true)
      .map(([key]) => key),
  );

  onKeysChange(keys: string[]): void {
    this.hasSelection.set(keys.length > 0);
    const group = this.control();
    for (const key of Object.keys(group.controls)) {
      const isChecked = keys.includes(key);
      // Only write on a real change. The multiselect echoes its current value on init,
      // and a no-op setValue would still emit valueChanges and mark the form dirty.
      if (group.controls[key].value !== isChecked) {
        group.controls[key].setValue(isChecked);
      }
    }
  }
}
